import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle } from 'lucide-react';

import { ServerLink } from '@/components/fleet/server-link';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useDegradedIngresses } from '@/hooks/use-degraded-ingresses';

/**
 * Dashboard card surfacing every ingress / v2ray_edge that the runtime
 * selector is currently excluding. Renders nothing when the fleet is
 * healthy — operators don't need a permanent "0 degraded" card adding
 * to dashboard chrome.
 *
 * Data source: GET /admin/fleet/degraded-ingresses (Redis-backed,
 * worker-written, 90s TTL). See backend service.
 */
export function DegradedIngressCard(): JSX.Element | null {
  const query = useDegradedIngresses();
  const items = query.data ?? [];
  if (items.length === 0) return null;

  return (
    <Card className="border-amber-300 dark:border-amber-900/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" aria-hidden />
          Degraded ingresses ({items.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-xs text-muted-foreground">
          Excluded from the runtime selector — all downstream exits stale. Each row recovers
          automatically when its exit heartbeats again.
        </p>
        <ul className="divide-y">
          {items.map((d) => (
            <li key={d.name} className="flex items-center justify-between gap-2 py-2">
              <div className="min-w-0">
                <ServerLink name={d.name} />
                <p className="text-xs text-muted-foreground">
                  {d.reason} — for {formatDistanceToNow(new Date(d.since))}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
